import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const ProductDisplay = ({
  selectedCategory = "all",
  selectedBrand = null,
  searchQuery = "",
}) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [itemsPerPage, setItemsPerPage] = useState(9);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("/products.json");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setProducts(data.products || []);
        setCategories(data.categories || []);
        setBrands(data.brands || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to get brand name from brand_id
  const getBrandName = (brandId) => {
    const brand = brands.find((b) => b.id === brandId);
    return brand ? brand.name : "unknown";
  };

  const getFilteredProducts = () => {
    let filtered = products;

    // Filter by category
    if (selectedCategory !== "all") {
      const category = categories.find((cat) => cat.class === selectedCategory);
      if (category) {
        filtered = filtered.filter(
          (product) => product.category_id === category.id
        );
      }
    }

    // Filter by brand if specified
    if (selectedBrand) {
      filtered = filtered.filter(
        (product) => product.brand_id === selectedBrand
      );
    }

    // Filter by search query
    if (searchQuery && searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setImageErrors(new Set()); // Clear image errors when filters change
  }, [selectedCategory, selectedBrand, searchQuery]);

  // Pagination calculations
  const totalPages =
    itemsPerPage === "all"
      ? 1
      : Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex =
    itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex =
    itemsPerPage === "all"
      ? filteredProducts.length
      : startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // window.scrollTo({ top: 350, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Combined pagination and items per page controls */}
      <div style={{ marginBottom: "20px" }}>
        {/* First row: Pagination and Items per page selector */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          {/* Items per page selector */}
          <div
            className="items-per-page-selector"
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <span style={{ fontSize: "14px", color: "#666" }}>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) =>
                handleItemsPerPageChange(
                  e.target.value === "all" ? "all" : parseInt(e.target.value)
                )
              }
              style={{
                padding: "5px 10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
                backgroundColor: "white",
                cursor: "pointer",
              }}
            >
              <option value={9}>9</option>
              <option value={27}>27</option>
              <option value={36}>36</option>
              <option value="all">All</option>
            </select>
            <span style={{ fontSize: "14px", color: "#666" }}>
              items per page
            </span>
          </div>

          {/* Pagination controls */}
          <div className="pagination">
            {totalPages > 1 && (
              <>
                {/* Previous button */}
                <button
                  className={`pagination-btn ${
                    currentPage === 1 ? "disabled" : ""
                  }`}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: "8px 12px",
                    margin: "0 2px",
                    border: "1px solid #ddd",
                    backgroundColor: currentPage === 1 ? "#f5f5f5" : "white",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    borderRadius: "4px",
                  }}
                >
                  ‹
                </button>

                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = 1;
                  let endPage = Math.min(totalPages, maxVisiblePages);

                  // Adjust window based on current page position
                  if (currentPage > maxVisiblePages) {
                    // When beyond initial pages, show current page at the end
                    startPage = currentPage - maxVisiblePages + 1;
                    endPage = currentPage;
                  } else if (
                    currentPage > Math.floor(maxVisiblePages / 2) &&
                    totalPages > maxVisiblePages
                  ) {
                    // When in middle of initial pages and there are more pages, start shifting
                    startPage = Math.max(
                      1,
                      currentPage - Math.floor(maxVisiblePages / 2)
                    );
                    endPage = Math.min(
                      totalPages,
                      startPage + maxVisiblePages - 1
                    );
                  }

                  // First page button (if not in current window)
                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        className="pagination-btn"
                        onClick={() => handlePageChange(1)}
                        style={{
                          padding: "8px 12px",
                          margin: "0 2px",
                          border: "1px solid #ddd",
                          backgroundColor: "white",
                          color: "#333",
                          cursor: "pointer",
                          borderRadius: "4px",
                        }}
                      >
                        1
                      </button>
                    );

                    // Ellipsis if there's a gap
                    if (startPage > 2) {
                      pages.push(
                        <span
                          key="ellipsis-start"
                          style={{
                            padding: "8px 4px",
                            margin: "0 2px",
                            color: "#666",
                          }}
                        >
                          ...
                        </span>
                      );
                    }
                  }

                  // Current window pages
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`pagination-btn ${
                          currentPage === i ? "active" : ""
                        }`}
                        onClick={() => handlePageChange(i)}
                        style={{
                          padding: "8px 12px",
                          margin: "0 2px",
                          border: "1px solid #ddd",
                          backgroundColor:
                            currentPage === i ? "#007bff" : "white",
                          color: currentPage === i ? "white" : "#333",
                          cursor: "pointer",
                          borderRadius: "4px",
                        }}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Last page button (if not in current window)
                  if (endPage < totalPages) {
                    // Ellipsis if there's a gap
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span
                          key="ellipsis-end"
                          style={{
                            padding: "8px 4px",
                            margin: "0 2px",
                            color: "#666",
                          }}
                        >
                          ...
                        </span>
                      );
                    }

                    pages.push(
                      <button
                        key={totalPages}
                        className="pagination-btn"
                        onClick={() => handlePageChange(totalPages)}
                        style={{
                          padding: "8px 12px",
                          margin: "0 2px",
                          border: "1px solid #ddd",
                          backgroundColor: "white",
                          color: "#333",
                          cursor: "pointer",
                          borderRadius: "4px",
                        }}
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}

                {/* Next button */}
                <button
                  className={`pagination-btn ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: "8px 12px",
                    margin: "0 2px",
                    border: "1px solid #ddd",
                    backgroundColor:
                      currentPage === totalPages ? "#f5f5f5" : "white",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                    borderRadius: "4px",
                  }}
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>

        {/* Second row: Product count and showing info */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "14px",
            color: "#666",
          }}
        >
          <div className="results-count">
            {filteredProducts.length} product
            {filteredProducts.length !== 1 ? "s" : ""} found
          </div>
          <div className="pagination-info">
            {itemsPerPage === "all"
              ? `Showing all ${filteredProducts.length} products`
              : `Showing ${startIndex + 1}-${Math.min(
                  endIndex,
                  filteredProducts.length
                )} of ${filteredProducts.length} products`}
          </div>
        </div>
      </div>

      <div className="products-grid">
        {filteredProducts.length === 0 ? (
          <div className="col-12 text-center py-5">
            <div className="no-products-found">
              <h4>No products found</h4>
              <p className="text-muted">
                Try adjusting your filters to see more products.
              </p>
            </div>
          </div>
        ) : (
          currentProducts.map((product, index) => (
            <Link
              key={index}
              to={`/brands/${getBrandName(
                product.brand_id
              )}/${encodeURIComponent(product.name)}`}
              className="product-card-link"
            >
              <div className="product-card">
                <div className="product-image-container">
                  <div className="brand-logo-overlay">
                    <img
                      src={`/images/brands/${getBrandName(
                        product.brand_id
                      )}.png`}
                      alt={getBrandName(product.brand_id)}
                      className="product-brand-logo"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                  {imageErrors.has(product.name) ? (
                    <img
                      src="./../../images/products/placeholder.svg"
                      alt="Image not available"
                      className="product-image"
                    />
                  ) : (
                    <img
                      src={`/images/products/${product.name
                        .replace(/\//g, "-")
                        .replace(/\*/g, "-")}.jpg`}
                      alt={product.name}
                      className="product-image"
                      onError={(e) => {
                        // Try JPEG format if JPG fails
                        if (e.target.src.endsWith(".jpg")) {
                          e.target.src = e.target.src.replace(".jpg", ".jpeg");
                        } else {
                          // Add to error set to show placeholder
                          setImageErrors(
                            (prev) => new Set([...prev, product.name])
                          );
                        }
                      }}
                    />
                  )}
                </div>
                <div className="product-content">
                  <div className="product-brand">{product.brand}</div>
                  <h3 className="product-title">{product.name}</h3>
                  <div className="product-model">
                    Model: {product.name.split(" ").pop()}
                  </div>
                  <div className="product-actions">
                    <span className="btn-view-details">View Details</span>
                  </div>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </>
  );
};

export default ProductDisplay;
